import React, { useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useGoals } from '../context/GoalsContext';
import { useIncome } from '../context/IncomeContext';
import { useSubscription } from '../context/SubscriptionContext';
import { AnalyticsService } from '../services/analyticsService';
import { useTheme } from '../context/ThemeContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { isWeb } from '../utils/platformUtils';

interface AnalyticsDashboardProps {
  onViewDetails?: () => void;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ onViewDetails }) => {
  const styles = useAnalyticsDashboardStyles();
  const { colors } = useTheme();
  const { goals } = useGoals();
  const { income } = useIncome();
  const { canShowDashboardCard } = useSubscription();
  const navigation = useNavigation();

  const analytics = useMemo(() => {
    const goalAnalytics = AnalyticsService.calculateGoalAnalytics(goals);
    const financialInsights = AnalyticsService.calculateFinancialInsights(goals, income);
    const predictiveAnalytics = AnalyticsService.calculatePredictiveAnalytics(goals);
    
    return { goalAnalytics, financialInsights, predictiveAnalytics };
  }, [goals, income]);

  // Debug: Log the values to diagnose multiple lines issue
  console.log('Financial Analysis - incomeAllocationEfficiency:', analytics.financialInsights.incomeAllocationEfficiency);
  console.log('Financial Analysis - savingsRateOptimization:', analytics.financialInsights.savingsRateOptimization);

  // Defensive: Use only the first value if array, else use as is
  const incomeAllocationValue = Array.isArray(analytics.financialInsights.incomeAllocationEfficiency)
    ? analytics.financialInsights.incomeAllocationEfficiency[0]
    : analytics.financialInsights.incomeAllocationEfficiency;
  const savingsRateValue = Array.isArray(analytics.financialInsights.savingsRateOptimization)
    ? analytics.financialInsights.savingsRateOptimization[0]
    : analytics.financialInsights.savingsRateOptimization;

  const renderProgressBar = (percentage: number, color: string = colors.primary.main) => (
    <View style={styles.progressContainer}>
      <View style={[styles.progressBar, { width: `${Math.min(percentage, 100)}%`, backgroundColor: color }]} />
    </View>
  );

  const renderMetricCard = (title: string, value: string | number, subtitle?: string, color?: string) => (
    <View style={[styles.metricCard, { borderLeftColor: color || colors.primary.main }]}>
      <Text style={styles.metricTitle}>{title}</Text>
      <Text
        style={[styles.metricValue, { color: color || colors.primary.main }]}
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {String(value)}
      </Text>
      {subtitle && <Text style={styles.metricSubtitle}>{subtitle}</Text>}
    </View>
  );

  const renderTrendChart = (data: Array<{ month: string; progress: number }>, title: string) => {
    if (data.length === 0) return null;

    const maxValue = Math.max(...data.map(d => d.progress));

    return (
      <View style={styles.chartContainer}>
        <Text style={styles.chartTitle}>{title}</Text>
        <View style={styles.chartBars}>
          {data.map((item, index) => {
            const height = maxValue > 0 ? (item.progress / maxValue) * 60 : 0;
            return (
              <View key={index} style={styles.chartBarContainer}>
                <View style={[styles.chartBar, { height }]} />
                <Text style={styles.chartLabel}>{item.month.slice(-2)}</Text>
              </View>
            );
          })}
        </View>
      </View>
    );
  };

  const renderRecommendations = (recommendations: string[]) => (
    <View style={styles.recommendationsContainer}>
      <Text style={styles.sectionTitle}>💡 Smart Recommendations</Text>
      {recommendations.map((recommendation, index) => (
        <View key={index} style={styles.recommendationItem}>
          <Ionicons name="bulb-outline" size={16} color={colors.warning.main} />
          <Text style={styles.recommendationText}>{recommendation}</Text>
        </View>
      ))}
    </View>
  );

  const renderRiskAssessment = (riskAssessment: Record<string, 'low' | 'medium' | 'high'>) => {
    const riskCounts = Object.values(riskAssessment).reduce((counts, risk) => {
      counts[risk] = (counts[risk] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);

    return (
      <View style={styles.riskContainer}>
        <Text style={styles.sectionTitle}>⚠️ Risk Assessment</Text>
        <View style={styles.riskGrid}>
          <View style={[styles.riskItem, { backgroundColor: colors.success.light }]}>
            <Text style={styles.riskCount}>{riskCounts.low || 0}</Text>
            <Text style={styles.riskLabel}>Low Risk</Text>
          </View>
          <View style={[styles.riskItem, { backgroundColor: colors.warning.light }]}>
            <Text style={styles.riskCount}>{riskCounts.medium || 0}</Text>
            <Text style={styles.riskLabel}>Medium Risk</Text>
          </View>
          <View style={[styles.riskItem, { backgroundColor: colors.error.light }]}>
            <Text style={styles.riskCount}>{riskCounts.high || 0}</Text>
            <Text style={styles.riskLabel}>High Risk</Text>
          </View>
        </View>
      </View>
    );
  };

  if (!canShowDashboardCard('monthlyContribution')) {
    return (
      <View style={styles.upgradeContainer}>
        <Text style={styles.upgradeTitle}>📊 Enhanced Analytics</Text>
        <Text style={styles.upgradeText}>
          Unlock detailed analytics, performance insights, and smart recommendations with Standard or Premium plans.
        </Text>
        <TouchableOpacity style={styles.upgradeButton} onPress={() => navigation.navigate('Subscription')}>
          <Text style={styles.upgradeButtonText}>Upgrade Plan</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const ScrollBody = isWeb ? View : ScrollView;
  const scrollBodyProps = isWeb
    ? { style: styles.containerWeb }
    : { style: styles.container, showsVerticalScrollIndicator: false as const };

  return (
    <ScrollBody {...scrollBodyProps}>
      {/* Goal Performance Analytics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📈 Goal Performance</Text>
        <View style={styles.metricsGrid}>
          {renderMetricCard(
            'Completion Rate',
            `${analytics.goalAnalytics.completionRate.toFixed(2)}%`,
            'Goals completed successfully',
            colors.success.main
          )}
          {renderMetricCard(
            'Avg Completion Time',
            `${Math.round(analytics.goalAnalytics.averageCompletionTime)} days`,
            'Time to complete goals',
            colors.secondary.main
          )}
          {renderMetricCard(
            'Income Allocation',
            `${Number(incomeAllocationValue).toFixed(2)}%`,
            'Of income allocated to goals',
            colors.primary.main
          )}
        </View>
      </View>

      {/* Success Rate by Priority */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>🎯 Success by Priority</Text>
        <View style={styles.priorityContainer}>
          {Object.entries(analytics.goalAnalytics.successRateByPriority).map(([priority, rate]) => (
            <View key={priority} style={styles.priorityItem}>
              <Text style={styles.priorityLabel}>{priority}</Text>
              <View style={styles.priorityProgress}>
                {renderProgressBar(rate, 
                  priority === 'Essential' ? colors.error.main :
                  priority === 'Important' ? colors.warning.main :
                  colors.success.main
                )}
                <Text style={styles.priorityRate}>{rate.toFixed(2)}%</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Type Performance */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>📊 Performance by Type</Text>
        <View style={styles.typePerformanceContainer}>
          {Object.entries(analytics.goalAnalytics.typePerformance).map(([type, performance]) => (
            <View key={type} style={styles.typePerformanceItem}>
              <View style={styles.typeHeader}>
                <Text style={styles.typeIcon}>
                  {type === 'Savings' ? '💰' : type === 'Debt' ? '💳' : '📈'}
                </Text>
                <Text style={styles.typeName}>{type}</Text>
                <Text style={styles.typeCount}>({performance.count})</Text>
              </View>
              <View style={styles.typeStats}>
                <Text style={styles.typeStat}>Success: {performance.successRate.toFixed(2)}%</Text>
                <Text style={styles.typeStat}>
                  Avg Time: {Math.round(performance.avgTime)} days
                </Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Financial Insights */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>💰 Financial Insights</Text>
        <View style={styles.metricsGrid}>
          {renderMetricCard(
            'Debt Payoff Speed',
            `${analytics.financialInsights.debtPayoffAcceleration.toFixed(2)}x`,
            'Faster than minimum payments',
            colors.secondary.main
          )}
          {renderMetricCard(
            'Savings Rate',
            `${Number(savingsRateValue).toFixed(2)}%`,
            'Of income saved',
            colors.success.main
          )}
        </View>
      </View>

      {/* Progress Trends */}
      {analytics.goalAnalytics.monthlyProgressTrend.length > 0 && (
        <View style={styles.section}>
          {renderTrendChart(analytics.goalAnalytics.monthlyProgressTrend, 'Monthly Progress Trend')}
        </View>
      )}

      {/* Risk Assessment */}
      {Object.keys(analytics.predictiveAnalytics.riskAssessment).length > 0 && (
        <View style={styles.section}>
          {renderRiskAssessment(analytics.predictiveAnalytics.riskAssessment)}
        </View>
      )}

      {/* Recommendations */}
      {analytics.predictiveAnalytics.recommendations.length > 0 && (
        <View style={styles.section}>
          {renderRecommendations(analytics.predictiveAnalytics.recommendations)}
        </View>
      )}

      {/* View Details Button — web uses header link on Report page */}
      {onViewDetails && !isWeb ? (
        <TouchableOpacity style={styles.viewDetailsButton} onPress={onViewDetails}>
          <Text style={styles.viewDetailsText}>View Detailed Analytics</Text>
          <Ionicons name="arrow-forward" size={16} color={colors.primary.main} />
        </TouchableOpacity>
      ) : null}
    </ScrollBody>
  );
};

const useAnalyticsDashboardStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.default,
  },
  containerWeb: {
    backgroundColor: colors.background.default,
  },
  section: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 16,
  },
  metricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  metricCard: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 12,
    borderLeftWidth: 4,
    flex: 1,
    marginHorizontal: 4,
    marginBottom: 8,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  metricTitle: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 4,
  },
  metricValue: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  metricSubtitle: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  priorityContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  priorityItem: {
    marginBottom: 12,
  },
  priorityLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  priorityProgress: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  priorityRate: {
    fontSize: 12,
    color: colors.text.secondary,
    marginLeft: 8,
    minWidth: 40,
  },
  progressContainer: {
    height: 8,
    backgroundColor: colors.grey[200],
    borderRadius: 4,
    flex: 1,
    overflow: 'hidden',
  },
  progressBar: {
    height: '100%',
    borderRadius: 4,
  },
  typePerformanceContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  typePerformanceItem: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  typeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  typeIcon: {
    fontSize: 20,
    marginRight: 8,
  },
  typeName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    flex: 1,
  },
  typeCount: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  typeStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  typeStat: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  chartContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 16,
  },
  chartBars: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-around',
    height: 80,
  },
  chartBarContainer: {
    alignItems: 'center',
    flex: 1,
  },
  chartBar: {
    width: 20,
    backgroundColor: colors.button.primary,
    borderRadius: 2,
    marginBottom: 8,
  },
  chartLabel: {
    fontSize: 10,
    color: colors.text.secondary,
  },
  riskContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  riskGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  riskItem: {
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    flex: 1,
    marginHorizontal: 4,
  },
  riskCount: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
  },
  riskLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    marginTop: 4,
  },
  recommendationsContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
  },
  recommendationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  recommendationText: {
    fontSize: 14,
    color: colors.text.primary,
    marginLeft: 8,
    flex: 1,
  },
  viewDetailsButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 24,
    borderRadius: 12,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  viewDetailsText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.primary.main,
    marginRight: 8,
  },
  upgradeContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    margin: 16,
    alignItems: 'center',
  },
  upgradeTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 12,
  },
  upgradeText: {
    fontSize: 14,
    color: colors.text.secondary,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 20,
  },
  upgradeButton: {
    backgroundColor: colors.button.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  upgradeButtonText: {
    color: colors.button.primaryText,
    fontSize: 16,
    fontWeight: 'bold',
  },
}));

export default AnalyticsDashboard;
